import { useCallback, useEffect, useState } from "react";
import { useGlobalStore } from "../context/UserContext/userContext";
import {
  USER_UI_PREFERENCES_CHANGE_EVENT,
  USER_UI_PREFERENCES_STORAGE_KEY,
  buildUiPreferencesUserKey,
  readUserUiPreference,
  writeUserUiPreference,
} from "../utils/userUiPreferences";

function useCurrentUiPreferencesUserKey() {
  const authSource = useGlobalStore((s) => s.authSource);
  const creUserId = useGlobalStore((s) => s.creUser?.userId);
  const radicalUserId = useGlobalStore((s) => s.radicalUser?.userId);
  return buildUiPreferencesUserKey(
    authSource,
    creUserId || radicalUserId,
  );
}

/**
 * Per-user, front-end-only UI preference (localStorage). Stays in sync across
 * components and browser tabs.
 *
 * @param {string} prefKey - see UI_PREFERENCE
 * @param {*} defaultValue
 * @returns {[any, (value: any) => void]}
 */
export function useUserUiPreference(prefKey, defaultValue) {
  const userKey = useCurrentUiPreferencesUserKey();
  const [value, setValue] = useState(() =>
    readUserUiPreference(userKey, prefKey, defaultValue),
  );

  // Re-read when the signed-in user changes.
  useEffect(() => {
    setValue(readUserUiPreference(userKey, prefKey, defaultValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey, prefKey]);

  useEffect(() => {
    const onLocalChange = (e) => {
      const d = e?.detail;
      if (d && d.userKey === userKey && d.prefKey === prefKey) {
        setValue(d.value);
      }
    };
    const onStorage = (e) => {
      if (e.key === USER_UI_PREFERENCES_STORAGE_KEY) {
        setValue(readUserUiPreference(userKey, prefKey, defaultValue));
      }
    };
    window.addEventListener(USER_UI_PREFERENCES_CHANGE_EVENT, onLocalChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(
        USER_UI_PREFERENCES_CHANGE_EVENT,
        onLocalChange,
      );
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey, prefKey]);

  const update = useCallback(
    (next) => {
      setValue(next);
      writeUserUiPreference(userKey, prefKey, next);
    },
    [userKey, prefKey],
  );

  return [value, update];
}
